/**
 * Where each GLB node sits under the instancing scene, and the node data its override gives it:
 * the shared reading the scene cull and the typed children attached into the GLB both start from.
 */
import { parseGeometryInstance3D } from '../../../nodes/3d/geometryinstance3d/parser';
import { visibilityParentOf } from '../../../godot/scene/visibilityParent';
import { godotNodePaths } from '../../../resources/formats/glb/godotNodes';
import { joinPath, parentPathOf } from '../../../utils/nodePath';
import { glbObjectName } from './glbHierarchy';
import { isApplicableGlbOverride, resolveGlbOverrideTarget } from './glbNodeOverrides';
/**
 * The path of the Godot node an entry's object is, below the GLB root. A mesh that holds children
 * stands for two nodes, and the last is the mesh below its Skeleton3D, which the cull measures. An
 * object that stands for no node, such as a bone, keeps its `relPath`.
 */
function godotPathBelowRoot(entry) {
    return godotNodePaths(entry.object).at(-1) ?? entry.relPath;
}
/**
 * Each GLB node below the root in `entries` order, as the instancing scene leaves it. A node
 * without an override of its own is at its Godot path under the root, and takes its parent's
 * visibility parent.
 */
export function glbNodeLinks(object, entries, root, uniquePaths) {
    const overrides = overridesByTarget(object, entries, root.overrides);
    const passedOn = new Map();
    return entries.map((entry) => {
        const override = overrides.get(entry.object);
        const path = root.path === null
            ? null
            : joinPath(root.path, override ? joinPath(override.instanceSubPath ?? '', override.name) : godotPathBelowRoot(entry));
        const properties = parseGeometryInstance3D({ type: 'node', attributes: { name: glbObjectName(entry.object) } }, override?.rawProperties ?? {});
        // `has`, not `??`: a parent whose own link resolves to none passes none on.
        const parent = parentPathOf(entry.relPath);
        const inherited = parent !== null && passedOn.has(parent) ? (passedOn.get(parent) ?? null) : root.visibilityParent;
        const parentPath = path === null
            ? inherited
            : visibilityParentOf(path, properties.visibility_parent, inherited, uniquePaths);
        passedOn.set(entry.relPath, parentPath);
        return { entry, properties, place: { path, parentPath } };
    });
}
/** Each GLB object an override node applies to, with the first override that names it. */
function overridesByTarget(object, entries, overrides) {
    const byTarget = new Map();
    for (const override of overrides) {
        if (!isApplicableGlbOverride(override))
            continue;
        const target = resolveGlbOverrideTarget(object, entries, override);
        if (target && !byTarget.has(target))
            byTarget.set(target, override);
    }
    return byTarget;
}
//# sourceMappingURL=glbNodeLinks.js.map